import './App.css';

// Libraries
import React, { useEffect } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { ApolloProvider, client } from 'utils/graphql/apollo';
import { useLocalStorage } from 'utils/localstorage.util';
import User from 'utils/user.util';

// Component & Views
import useToken from 'components/useToken';
import Header from 'components/Header/Header';
import SelectProperty from 'views/SelectProperty/SelectProperty';
import ContactWebform from 'views/ContactWebform/ContactWebform';
import Login from 'views/Login/Login';
import Logout from 'views/Logout/Logout';
import Settings from 'views/Settings/Settings';
import Footer from 'components/Footer/Footer';
import GiftCardInventory from 'views/GiftCardInventory/GiftCardInventory';
import GiftCardInventoryV2 from 'views/GiftCardInventory/GiftCardInventoryV2';
import AccessWrapper from 'components/AccessWrapper/AccessWrapper';
import Unification from 'views/Unification/Unification';
import B2BRewards from 'views/B2BRewards/B2BRewards';
import CampaignListTrigger from 'views/CampaignListTrigger/CampaignListTrigger';
import TokenFinder from 'views/TokenFinder/TokenFinder';
import Reports from 'views/Reports/Reports';

export const PROPERTY_NAMESPACE = 'cwf-property';

function App() {
    let navigate = useNavigate();
    const { pathname } = useLocation();
    const { token, setToken, isAuth } = useToken();
    const [property, setProperty] = useLocalStorage(PROPERTY_NAMESPACE);

    const pathsThatNeedProperty = [
        '/contactform',
        '/giftcardinventory',
        '/unification',
        '/campaigntrigger',
    ];

    // all hooks must be before the token check
    // to always render the same amount of hooks
    useEffect(() => {
        if (!property && pathsThatNeedProperty.find((path) => path === pathname)) {
            navigate('/selectproperty', { state: { from: null } });
        }
    }, [pathname, property, navigate]);

    if (!token) {
        User.clear();
        return (
            <>
                <Header />
                <Login setToken={setToken} />
            </>
        );
    }

    // Check if user is authenticated on each render
    isAuth()
        .then((isAuthenticated) => {
            if (!isAuthenticated) {
                setToken(null);
            }
        })
        .catch(() => {
            setToken(null);
        });

    return (
        <div className="app">
            <Header isLoggedIn={true} />
            <ApolloProvider client={client}>
                <AccessWrapper>
                    <Routes>
                        <Route path="/" element={<Navigate to="/selectproperty" />}></Route>
                        <Route path="/login" element={<Navigate to="/selectproperty" />}></Route>
                        <Route
                            path="/selectproperty"
                            element={<SelectProperty setProperty={setProperty} />}
                        ></Route>
                        <Route path="/tokenfinder" element={<TokenFinder />}></Route>
                        <Route path="/reports" element={<Reports />}></Route>
                        <Route path="/settings/*" element={<Settings />}></Route>
                        <Route
                            path="/logout"
                            element={<Logout setToken={setToken} setProperty={setProperty} />}
                        ></Route>
                        {property && (
                            <>
                                <Route
                                    path="/contactform"
                                    element={<ContactWebform property={property} />}
                                ></Route>
                                <Route
                                    path="/giftcardinventory"
                                    element={<GiftCardInventoryV2 property={property} />}
                                ></Route>
                                <Route
                                    path="/giftcardinventory/legacy"
                                    element={<GiftCardInventory property={property} />}
                                ></Route>
                                <Route
                                    path="/unification"
                                    element={<Unification property={property}></Unification>}
                                ></Route>
                                <Route
                                    path="/b2brewards"
                                    element={<B2BRewards property={property}></B2BRewards>}
                                ></Route>
                                <Route
                                    path="/campaigntrigger"
                                    element={<CampaignListTrigger property={property} />}
                                ></Route>
                            </>
                        )}
                    </Routes>
                </AccessWrapper>
            </ApolloProvider>
            <Footer selectedProperty={property} />
        </div>
    );
}

export default App;
